import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Users, TrendingUp, Award, BookOpen, Search, AlertTriangle,
  ChevronDown, ChevronUp, X, Send, ChevronRight, BarChart2,
  Flame, Zap, Clock, CheckCircle, MessageSquare, GraduationCap, UserPlus
} from '../components/CustomIcons';
import StreakCalendar from '../components/StreakCalendar';
import EmptyState from '../components/EmptyState';
import WelcomeModal from '../components/WelcomeModal';
import { useMarkOnboardingPath } from '../components/WelcomeModal';

function StatCard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="card" style={{ padding: '20px', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}>{label}</p>
          <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)', lineHeight: 1 }}>{value}</p>
          {sub && <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{sub}</p>}
        </div>
        <div style={{ width: 36, height: 36, borderRadius: '10px', background: `${color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} style={{ color }} />
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ pct }) {
  return (
    <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden', width: '100%' }}>
      <div style={{ height: '100%', background: 'var(--orange)', borderRadius: 3, width: `${Math.min(pct, 100)}%`, transition: 'width 0.4s ease' }} />
    </div>
  );
}

function LearnerDetailPanel({ learner, onClose, authFetch }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!learner) return;
    setDetail(null);
    authFetch(`/admin/caseload/${learner.id}`)
      .then(r => setDetail(r.data))
      .catch(e => console.error('detail fetch error:', e));
  }, [learner?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const res = await authFetch(`/admin/notes/${learner.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { content: note.trim() },
      });
      const raw = res.data;
      const newNote = {
        id: raw.id || String(Date.now()),
        staff_name: raw.staff_name || raw.author_name || 'Staff',
        note: raw.note || raw.content || note.trim(),
        created_at: raw.created_at || new Date().toISOString(),
      };
      setDetail(prev => prev ? { ...prev, notes: [newNote, ...(prev.notes || [])] } : prev);
      setNote('');
    } catch (e) { console.error('add-note error:', e); }
    finally { setSaving(false); }
  };

  const profile = detail?.learner?.profile || learner?.profile || {};
  const weekly = detail?.weekly_summary || {};
  const loginDates = detail?.login_dates || [];
  const credentials = detail?.credentials || [];
  const notes = detail?.notes || [];

  const pathwayName = (profile.current_pathway || '').replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase()) || 'Not enrolled';
  const progress = (profile.pathway_progress || {})[profile.current_pathway || ''] || 0;

  if (!learner) return null;

  return (
    <div
      data-testid="learner-detail-panel"
      role="dialog"
      aria-modal="true"
      aria-label={`Learner details for ${learner.first_name} ${learner.last_name}`}
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '440px', maxWidth: '100vw',
        background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-default)',
        zIndex: 50, overflowY: 'auto', padding: '0',
        animation: 'slideInRight 0.2s ease',
      }}>
      <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

      {/* Panel header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)', padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', margin: 0 }}>
              {learner.first_name} {learner.last_name}
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{learner.email}</p>
          </div>
          <button onClick={onClose} aria-label="Close detail panel"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '6px', borderRadius: '6px' }}>
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginTop: '12px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: '8px' }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'credentials', label: `Certs (${credentials.length})` },
            { id: 'notes', label: `Notes (${notes.length})` },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              aria-pressed={tab === t.id}
              data-testid={`instructor-panel-tab-${t.id}`}
              style={{
                flex: 1, padding: '5px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif',
                background: tab === t.id ? 'var(--bg-surface)' : 'transparent',
                color: tab === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '20px' }}>
        {/* Quick stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '20px' }}>
          {[
            { label: 'Credits', value: (profile.credits || 0).toLocaleString(), icon: Zap, color: '#F97316' },
            { label: 'Streak', value: `${profile.streak_days || 0}d`, icon: Flame, color: '#F59E0B' },
            { label: 'Credentials', value: credentials.length || learner.credential_count || 0, icon: Award, color: '#22C55E' },
            { label: 'Lessons/Wk', value: weekly.lessons_this_week ?? '…', icon: BookOpen, color: '#3B82F6' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} style={{ background: 'var(--bg-elevated)', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
              <Icon size={13} style={{ color, marginBottom: '4px' }} aria-hidden="true" />
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '18px', color: 'var(--text-primary)', marginBottom: '2px' }}>{value}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Overview tab */}
        {tab === 'overview' && (
          <>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>{pathwayName}</span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--orange)' }}>{progress}%</span>
              </div>
              <ProgressBar pct={progress} />
            </div>
            {loginDates.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', fontFamily: 'Inter, sans-serif' }}>Activity Calendar</p>
                <StreakCalendar loginDates={loginDates} />
              </div>
            )}
            {!detail && (
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>Loading details…</p>
            )}
          </>
        )}

        {/* Credentials tab */}
        {tab === 'credentials' && (
          <div>
            {credentials.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {credentials.map((c, i) => (
                  <div key={c.id || i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: '8px' }}>
                    <CheckCircle size={14} style={{ color: '#22C55E', flexShrink: 0 }} aria-hidden="true" />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{c.title || c.name || 'Credential'}</p>
                      <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>{new Date(c.date_earned || c.issued_at || Date.now()).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '30px 0' }}>No credentials earned yet.</p>
            )}
          </div>
        )}

        {/* Notes tab */}
        {tab === 'notes' && (
          <div>
            {/* Add note */}
            <div style={{ marginBottom: '16px' }}>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Add an instructor note…"
                aria-label="Add instructor note"
                data-testid="instructor-note-input"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-default)',
                  background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '13px',
                  fontFamily: 'Inter, sans-serif', resize: 'vertical', minHeight: '80px', boxSizing: 'border-box',
                }}
              />
              <button onClick={addNote} disabled={saving || !note.trim()}
                data-testid="instructor-add-note-btn"
                style={{
                  marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px',
                  borderRadius: '7px', border: 'none', background: 'var(--orange)', color: '#111111',
                  fontSize: '12px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif',
                  cursor: saving ? 'wait' : 'pointer', opacity: saving || !note.trim() ? 0.5 : 1,
                }}>
                <Send size={12} aria-hidden="true" />
                {saving ? 'Saving…' : 'Add Note'}
              </button>
            </div>
            {/* Notes list */}
            {notes.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {notes.map((n, i) => (
                  <div key={n.id || i} style={{ padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)' }}>{n.staff_name}</span>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{new Date(n.created_at).toLocaleDateString()}</span>
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--text-primary)', margin: 0, lineHeight: 1.5 }}>{n.note}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No notes yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Instructor() {
  const { authFetch, user } = useAuth();
  const [learners, setLearners] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pathwayFilter, setPathwayFilter] = useState('all');
  const [sortBy, setSortBy] = useState('progress');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  const facility = user?.facility || user?.profile?.facility || '';
  const assignedPathways = user?.profile?.assigned_pathways || [];

  useMarkOnboardingPath('/instructor');

  useEffect(() => {
    const q = facility ? `?facility=${encodeURIComponent(facility)}` : '';
    Promise.all([
      authFetch(`/admin/caseload${q}`),
      authFetch(`/admin/at-risk${q}`),
    ])
      .then(([lRes, aRes]) => {
        setLearners(lRes.data || []);
        setAtRisk(aRes.data || []);
      })
      .catch(e => console.error('instructor load error:', e))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived pathway list for filter dropdown — restricted to assigned pathways when set
  const pathways = useMemo(() => {
    const seen = new Set();
    learners.forEach(l => {
      const pw = l.profile?.current_pathway;
      if (pw) seen.add(pw);
    });
    const all = [...seen];
    if (assignedPathways.length > 0) return all.filter(pw => assignedPathways.includes(pw));
    return all;
  }, [learners, assignedPathways]);

  // Filtered + sorted learners
  const filtered = useMemo(() => {
    let list = learners.filter(l => {
      const name = `${l.first_name} ${l.last_name} ${l.email}`.toLowerCase();
      const matchSearch = !search || name.includes(search.toLowerCase());
      const matchPathway = pathwayFilter === 'all' || l.profile?.current_pathway === pathwayFilter;
      return matchSearch && matchPathway;
    });
    list.sort((a, b) => {
      let va, vb;
      if (sortBy === 'progress') {
        va = (a.profile?.pathway_progress || {})[a.profile?.current_pathway || ''] || 0;
        vb = (b.profile?.pathway_progress || {})[b.profile?.current_pathway || ''] || 0;
      } else if (sortBy === 'streak') {
        va = a.profile?.streak_days || 0;
        vb = b.profile?.streak_days || 0;
      } else if (sortBy === 'name') {
        va = `${a.first_name} ${a.last_name}`;
        vb = `${b.first_name} ${b.last_name}`;
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      } else {
        va = a.credential_count || 0;
        vb = b.credential_count || 0;
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [learners, search, pathwayFilter, sortBy, sortDir]);

  const handleSort = useCallback((col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  }, [sortBy]);

  // Stats
  const avgProgress = useMemo(() => {
    if (!learners.length) return 0;
    const total = learners.reduce((sum, l) => {
      const pw = l.profile?.current_pathway;
      return sum + ((l.profile?.pathway_progress || {})[pw || ''] || 0);
    }, 0);
    return Math.round(total / learners.length);
  }, [learners]);

  const activeThisWeek = learners.filter(l => (l.profile?.streak_days || 0) > 0).length;

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ width: 32, height: 32, border: '3px solid var(--border-default)', borderTopColor: 'var(--orange)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
        <p style={{ color: 'var(--text-muted)', marginTop: '16px', fontSize: '14px' }}>Loading class data…</p>
      </div>
    );
  }

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronDown size={12} style={{ opacity: 0.3 }} aria-hidden="true" />;
    return sortDir === 'asc'
      ? <ChevronDown size={12} style={{ color: 'var(--orange)', transform: 'rotate(180deg)' }} aria-hidden="true" />
      : <ChevronDown size={12} style={{ color: 'var(--orange)' }} aria-hidden="true" />;
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <GraduationCap size={22} style={{ color: 'var(--orange)' }} aria-hidden="true" />
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '24px', color: 'var(--text-primary)', margin: 0 }}>
              Instructor Portal
            </h1>
            <span style={{ padding: '3px 10px', borderRadius: '20px', background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>
              Instructor
            </span>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
            {facility ? `${facility} — ` : ''}{learners.length} enrolled learners across your courses
          </p>
          {/* Assigned pathway chips */}
          {assignedPathways.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }} aria-label="Assigned pathways">
              {assignedPathways.map(pw => (
                <span
                  key={pw}
                  data-testid={`assigned-pathway-chip-${pw}`}
                  style={{
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                    fontFamily: 'Inter, sans-serif', background: 'rgba(249,115,22,0.12)',
                    color: 'var(--orange)', border: '1px solid rgba(249,115,22,0.3)',
                  }}>
                  {pw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BarChart2 size={14} style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Class avg: </span>
          <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--orange)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{avgProgress}%</span>
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '28px', flexWrap: 'wrap' }}>
        <StatCard label="Total Learners" value={learners.length} icon={Users} color="#F97316"
          sub={`${filtered.length} matching filters`} />
        <StatCard label="Avg Progress" value={`${avgProgress}%`} icon={TrendingUp} color="#22C55E"
          sub="across all pathways" />
        <StatCard label="Active This Week" value={activeThisWeek} icon={Flame} color="#F59E0B"
          sub={`${Math.round(activeThisWeek / Math.max(learners.length, 1) * 100)}% engagement`} />
        <StatCard label="At-Risk Students" value={atRisk.length} icon={AlertTriangle} color="#EF4444"
          sub={atRisk.length > 0 ? 'Review needed' : 'None flagged'} />
      </div>

      {/* At-risk alert panel */}
      {atRisk.length > 0 && (
        <div style={{ marginBottom: '24px', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '12px', overflow: 'hidden' }}>
          <button
            onClick={() => setAlertsExpanded(p => !p)}
            aria-expanded={alertsExpanded}
            data-testid="instructor-at-risk-toggle"
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: 'none', cursor: 'pointer',
              color: '#EF4444', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle size={14} aria-hidden="true" />
              {atRisk.length} At-Risk Student{atRisk.length > 1 ? 's' : ''} — Immediate Attention Needed
            </div>
            {alertsExpanded ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
          </button>
          {alertsExpanded && (
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {atRisk.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: '8px', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>{s.first_name} {s.last_name}</span>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                      {s.risk_reasons.map((r, i) => (
                        <span key={i} style={{ padding: '2px 8px', borderRadius: '12px', background: 'rgba(239,68,68,0.1)', color: '#EF4444', fontSize: '11px', fontWeight: 500 }}>{r}</span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedLearner(learners.find(l => l.id === s.id) || { id: s.id, first_name: s.first_name, last_name: s.last_name, email: s.email, profile: {} })}
                    data-testid={`instructor-risk-view-${s.id}`}
                    style={{ padding: '5px 12px', borderRadius: '7px', border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#EF4444', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    View
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} aria-hidden="true" />
          <input className="input-dark" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search learners…"
            aria-label="Search learners"
            data-testid="instructor-search"
            style={{ paddingLeft: '36px' }} />
        </div>
        <select
          value={pathwayFilter}
          onChange={e => setPathwayFilter(e.target.value)}
          aria-label="Filter by pathway"
          data-testid="instructor-pathway-filter"
          style={{
            padding: '8px 32px 8px 12px', borderRadius: '8px', border: '1px solid var(--border-default)',
            background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '13px',
            fontFamily: 'Inter, sans-serif', cursor: 'pointer', appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
          }}>
          <option value="all">All Pathways</option>
          {pathways.map(pw => (
            <option key={pw} value={pw}>{pw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {filtered.length} student{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Learner table */}
      <div className="card" style={{ overflow: 'hidden', padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Learner progress table">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-default)', background: 'var(--bg-elevated)' }}>
                {[
                  { col: 'name', label: 'Student', style: { minWidth: '180px', textAlign: 'left' } },
                  { col: 'pathway', label: 'Pathway', style: { minWidth: '160px', textAlign: 'left' } },
                  { col: 'progress', label: 'Progress', style: { minWidth: '140px', textAlign: 'left' } },
                  { col: 'streak', label: 'Streak', style: { width: '80px', textAlign: 'center' } },
                  { col: 'credentials', label: 'Certs', style: { width: '60px', textAlign: 'center' } },
                  { col: 'actions', label: '', style: { width: '70px' } },
                ].map(({ col, label, style: s }) => (
                  <th key={col}
                    onClick={col !== 'actions' && col !== 'pathway' ? () => handleSort(col) : undefined}
                    style={{
                      padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)',
                      fontFamily: 'Plus Jakarta Sans, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em',
                      cursor: (col !== 'actions' && col !== 'pathway') ? 'pointer' : 'default',
                      userSelect: 'none', ...s,
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: s.textAlign === 'center' ? 'center' : 'flex-start' }}>
                      {label}
                      {col !== 'actions' && col !== 'pathway' && <SortIcon col={col} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((learner, idx) => {
                const pw = learner.profile?.current_pathway || '';
                const pct = (learner.profile?.pathway_progress || {})[pw] || 0;
                const pwLabel = pw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                const streak = learner.profile?.streak_days || 0;
                const isSelected = selectedLearner?.id === learner.id;

                return (
                  <tr key={learner.id}
                    data-testid={`instructor-learner-row-${learner.id}`}
                    style={{
                      borderBottom: idx < filtered.length - 1 ? '1px solid var(--border-default)' : 'none',
                      background: isSelected ? 'rgba(249,115,22,0.06)' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isSelected ? 'rgba(249,115,22,0.06)' : 'transparent'; }}>

                    {/* Name */}
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>
                        {learner.first_name} {learner.last_name}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{learner.email}</div>
                    </td>

                    {/* Pathway */}
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{pwLabel || '—'}</span>
                    </td>

                    {/* Progress */}
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <ProgressBar pct={pct} />
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: pct >= 75 ? '#22C55E' : pct >= 40 ? 'var(--orange)' : 'var(--text-muted)', fontFamily: 'Plus Jakarta Sans, sans-serif', minWidth: '32px', textAlign: 'right' }}>
                          {pct}%
                        </span>
                      </div>
                    </td>

                    {/* Streak */}
                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        <Flame size={12} style={{ color: streak > 0 ? '#F59E0B' : 'var(--text-muted)' }} aria-hidden="true" />
                        <span style={{ fontSize: '12px', fontWeight: 600, color: streak > 0 ? '#F59E0B' : 'var(--text-muted)' }}>{streak}d</span>
                      </div>
                    </td>

                    {/* Credentials */}
                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                        {learner.credential_count || 0}
                      </span>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '12px 14px' }}>
                      <button
                        onClick={() => setSelectedLearner(isSelected ? null : learner)}
                        data-testid={`instructor-view-btn-${learner.id}`}
                        aria-label={`View details for ${learner.first_name} ${learner.last_name}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px',
                          borderRadius: '7px', border: `1px solid ${isSelected ? 'var(--orange)' : 'var(--border-default)'}`,
                          background: isSelected ? 'rgba(249,115,22,0.1)' : 'transparent',
                          color: isSelected ? 'var(--orange)' : 'var(--text-secondary)',
                          fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif',
                          transition: 'all 0.15s',
                        }}>
                        View
                        <ChevronRight size={12} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && learners.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '0' }}>
                    <EmptyState
                      icon={UserPlus}
                      title="No students yet"
                      subtitle="Students assigned to your pathways will appear here. Check back after learners enroll."
                      action={{ label: 'Invite Students', onClick: () => window.location.href = '/instructor' }}
                      testId="instructor-roster-empty"
                    />
                  </td>
                </tr>
              )}
              {filtered.length === 0 && learners.length > 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
                    No learners match your search or filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right-side detail panel */}
      {selectedLearner && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setSelectedLearner(null)}
            aria-hidden="true"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 49 }}
          />
          <LearnerDetailPanel
            learner={selectedLearner}
            onClose={() => setSelectedLearner(null)}
            authFetch={authFetch}
          />
        </>
      )}
      <WelcomeModal />
    </div>
  );
}
